import {
  AfterViewInit,
  Directive,
  ElementRef,
  EventEmitter,
  inject,
  Injector,
  Input,
  Output,
  ViewChild,
  NgZone,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { PaneDataLoaderService, IDefContentPane } from '@app/_v3/services/pane-data-loader.service';
import { STREAM_ROUTES } from '@app/core/services/bff/bff-v2.service';
import { ILibrarySorting } from '@app/library/library-sorting/library-sorting.component';

import { AnalyticsService, ContextService } from '@core/services';
import { ChangableComponent } from '@models/changable.component';
import { EContentPanelRoute, IContentFilterBlock, type Meeting } from 'lingo2-models';
import { Classroom, Collection, Content, CSchool, User, UserService } from 'lingo2-models';
import isEqual from 'lodash/isEqual';
import { DeviceDetectorService } from 'ngx-device-detector';
import { fromEvent } from 'rxjs';
import { takeUntil, debounceTime } from 'rxjs/operators';
import { ILibraryTitle } from '../../library-title/library-title.component';
interface IPaneDataType {
  users: User[];
  collections: Collection[];
  content: Content[];
  userServices: UserService[];
  classrooms: Classroom[];
  schools: CSchool[];
  meetings: Meeting[];
}
type TPaneDataType = keyof IPaneDataType;

@Directive()
export abstract class AbstractPane extends ChangableComponent implements AfterViewInit {
  @ViewChild('scrollSentinel', { static: false }) scrollSentinelRef: ElementRef;

  protected ngZone = inject(NgZone);
  protected readonly router = inject(Router);
  protected readonly _route = inject(ActivatedRoute);
  protected readonly injector = inject(Injector);

  protected readonly paneLoader: PaneDataLoaderService = inject(PaneDataLoaderService);
  protected readonly contextService = inject(ContextService);
  protected analytics = inject(AnalyticsService);
  protected readonly deviceService = inject(DeviceDetectorService);

  public _pane: IDefContentPane;
  public title: string | string[];
  public blockTitle: Partial<ILibraryTitle> = {};
  public sorting: Partial<ILibrarySorting> = {};
  public isVisible = false;
  public addLink = '';
  public titleLink = '';
  public seeAllLink = '';
  public title_link = '';
  public classroomDetails = []; // Todo. Refactor
  public userServiceDetails = []; // Todo. Refactor

  // Pagination and content things
  private scrollSub: any;
  public _loadedItemsCount = 0;
  private currentPage = 1;
  private intersectionObserver: IntersectionObserver;
  private isLoadingNextPage = false;

  @Output() public loadFinished = new EventEmitter<boolean>();
  public isDebug$ = this.contextService.debug$;

  // NB! OnDestroy
  override ngOnDestroy(): void {
    this.intersectionObserver?.disconnect();
    if (this.scrollSub) {
      this.scrollSub.unsubscribe();
    }
    super.ngOnDestroy();
  }

  @Input()
  set pane(value: IDefContentPane) {
    if (!value || isEqual(this._pane, value)) {
      return;
    }
    this._pane = { ...value };
    /* TODO
    ИСправить когда сделаем пагинацию на беке или переведем все на новый бфф
    Дляработы пагинации там где передается totalPages = 0 в бфф
    */
    // if (this._pane.pagination && this._pane.pagination.totalPages === 0) {
    //   this._pane.pagination.totalPages = 20;
    // }
    /*
      END TODO
    */

    this.init();
  }

  get pane(): IDefContentPane {
    return this._pane;
  }
  get loaded(): boolean {
    return this._pane && this._pane.loaded;
  }

  get loadedItemsCount(): number {
    return this._loadedItemsCount;
  }

  get filterPanels(): IContentFilterBlock[] {
    return this._pane?.filters || this._pane.contentItems || this._pane.payload__filter || [];
  }

  get user(): User {
    return this._pane?.user;
  }

  // ...
  protected getContentItems<K extends TPaneDataType>(): IPaneDataType[K] {
    const raw = this._pane.contentItems || [];
    return Array.isArray(raw) ? (raw as IPaneDataType[K]) : ([] as IPaneDataType[K]);
  }

  get rawContentItems() {
    // back compat.
    return this.getContentItems();
  }

  get users(): User[] {
    return this.getContentItems<'users'>();
  }
  get meetings(): Meeting[] {
    return this.getContentItems<'meetings'>();
  }
  get userServices(): UserService[] {
    return this.getContentItems<'userServices'>();
  }
  get schools(): Partial<CSchool[]> {
    return this.getContentItems<'schools'>();
  }
  get classrooms(): Classroom[] {
    return this.getContentItems<'classrooms'>();
  }
  get collections(): Partial<Collection[]> {
    return this.getContentItems<'collections'>();
  }
  get contents(): Partial<Content[]> {
    return this.getContentItems<'content'>();
  }

  ngAfterViewInit(): void {
    this.initIntersectionObserver();
    this.initScrollListener();
  }

  private initScrollListener() {
    this.ngZone.runOutsideAngular(() => {
      this.scrollSub = fromEvent(window, 'scroll', { passive: true })
        .pipe(debounceTime(50), takeUntil(this.destroyed$))
        .subscribe(() => {
          this.ngZone.run(() => {
            this.handleScroll();
          });
        });
    });
  }

  protected handleScroll() {
    if (!this.scrollSentinelRef || !this._pane?.infinity || this.isLoadingNextPage) return;

    const sentinel = this.scrollSentinelRef.nativeElement;
    const rect = sentinel.getBoundingClientRect();
    const isVisible = rect.top <= window.innerHeight * 2;
    if (isVisible && this.hasMorePages()) {
      this.ngZone.run(() => {
        this.loadNextPage();
      });
    }
  }

  protected init(): void {
    const { routerUrlAll, routerUrlCreate, title_link, title } = this._pane || ({} as IDefContentPane);
    this.title = title || '';
    this.addLink = routerUrlCreate || '';
    this.seeAllLink = this._pane.infinity ? '' : routerUrlAll || '';
    this.titleLink = this.seeAllLink;
    this.title_link = title_link || '';

    this.blockTitle = {
      ...this.blockTitle,
      title: this.title,
      subtitle: this._pane?.subtitle || '',
      add_link: this.addLink,
      see_all_link: this.seeAllLink,
      title_link: this.titleLink,
      new_title_link: this.title_link,
    };
  }

  protected abstract onContentLoaded(viaQuery?: boolean): void;

  public load(): void {
    if ((this as any).load !== AbstractPane.prototype.load) {
      throw new Error('Override of load() is forbidden!');
    }
    this.loadPane();
  }

  public loadPane(force = false): void {
    // TODO. Temp shit to relax team
    if (this._pane.baseRoute === EContentPanelRoute.collections) {
      this._pane.contentFilter = undefined;
      // TODO ASAP — REFACTOR ALL *Filter Things. Create universal filter.
    }

    if (STREAM_ROUTES.includes(this._pane.baseRoute) || !!this._pane?.contentItems?.length) {
      this._loadedItemsCount = this.countLoadedItems();
      this.loadDataFinished(true);
      return;
    }

    // /TODO. Temp shit to relax team

    this.paneLoader
      .loadPane(this._pane, this.destroyed$, force)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((res) => {
        this._loadedItemsCount = this.countLoadedItems();
        this.loadDataFinished(force ? false : this.currentPage > 1);
      });
  }

  private initIntersectionObserver(): void {
    if (!this.scrollSentinelRef?.nativeElement || !this._pane?.infinity) return;

    this.intersectionObserver?.disconnect();
    const scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
    this.intersectionObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !this.isLoadingNextPage && this.hasMorePages()) {
          this.loadNextPage().finally(() => {
            window.scrollTo({ top: scrollPosition });
          });
        }
      },
      {
        root: null,
        rootMargin: '600px',
        threshold: 0.1,
      },
    );

    // NB! Don;t change — we need it to prevent all pages loading at same time!
    setTimeout(() => {
      if (this.scrollSentinelRef?.nativeElement) {
        this.intersectionObserver.observe(this.scrollSentinelRef.nativeElement);
      }
    }, 500);
  }

  public loadNextPage(): Promise<void> {
    return new Promise((resolve) => {
      const pagination = this._pane?.pagination;
      const totalPages = pagination?.totalPages ?? 20;
      if (!this._pane?.infinity || this.currentPage >= totalPages || this.isLoadingNextPage) {
        resolve();
        return;
      }

      this.currentPage++;
      this._pane.pagination = {
        ...pagination,
        page: this.currentPage,
      };

      this._pane.loaded = false;
      this.isLoadingNextPage = true;

      this.paneLoader
        .loadPane(this._pane, this.destroyed$, true)
        .pipe(takeUntil(this.destroyed$))
        .subscribe({
          next: (res) => {
            this._loadedItemsCount = this.countLoadedItems();
            this.loadDataFinished(true);
            resolve();
          },
          error: () => resolve(),
        });
    });
  }

  private hasMorePages(): boolean {
    if (!this._pane?.pagination) {
      return false;
    }
    const pagination = this._pane?.pagination;
    if (pagination.page >= pagination.totalPages) {
      return false;
    }

    return true;
    return !pagination || pagination.page < pagination.totalPages;
  }

  protected countLoadedItems(): number {
    return this.getContentItems().length;
  }

  protected loadDataFinished(viaQuery: boolean): void {
    this._pane.loaded = true;
    this._pane.loading = false;
    this.isLoadingNextPage = false;
    this.onContentLoaded(viaQuery);
    this.isVisible = this.isVisible || !!this.countLoadedItems();
    this.loadFinished.emit(this.hasContent());
    this.detectChanges();
  }

  public hasContent(): boolean {
    return !!(this._pane?.user || (this._pane?.contentItems?.length ?? 0) > 0);
  }

  protected calculateTitle(): string | string[] {
    return this._pane?.title || '';
  }

  protected loadMeetings() {
    this.paneLoader.loadMeetings(this._pane).subscribe(() => this.detectChanges());
  }

  protected loadSchools(_f?: any) {
    this._pane.filter = { ...this._pane.filter, use: 'school' };
    this.loadPane();
  }

  protected loadContent(_f?: any) {
    this._pane.filter = { ...this._pane.filter, use: 'content' };
    this.loadPane();
  }

  protected loadUserServices(_f?: any) {}

  protected loadMeetingsReviews(_f?: any) {}
}
